import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
    Box, Drawer, Toolbar, List,
    Typography,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemButton,
    ListItemText,
    Collapse,
    AppBar as MuiAppBar,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useLocation } from 'react-router-dom';
import TableViewIcon from '@mui/icons-material/TableView';
import SourceIcon from '@mui/icons-material/Source';
import CallIcon from '@mui/icons-material/Call';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';

const drawerWidth = 240;

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up('md')]: {
        ...(open && {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: `${drawerWidth}px`,
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        }),
    },
}));


export default function Navbar(props) {
    const { open, setOpen, admin } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();
    const [isMasters, setIsMasters] = useState(false);
    const [isContent, setIsContent] = useState(false);
    const [isUser, setIsUser] = useState(false);
    const [isSetting, setIsSetting] = useState(false);
    const [isAccounts, setIsAccounts] = useState(false);
    let permissions = [];
    try {
        if (admin?.Role?.permissions) {
            permissions = typeof admin.Role.permissions === 'string'
                ? JSON.parse(admin.Role.permissions)
                : admin.Role.permissions;
        }
    } catch (e) {
        console.error("Error parsing permissions:", e);
    }

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);
    const handleClick = () => setIsMasters(!isMasters);
    const handleClickContent = () => setIsContent(!isContent);
    const handleClickUser = () => setIsUser(!isUser);
    const handleClickSetting = () => setIsSetting(!isSetting);
    const handleClickAccounts = () => setIsAccounts(!isAccounts);

    const handleLinkClick = () => {
        if (isMobile) {
            setOpen(false);
        }
    };

    const isAccountant = admin?.Role?.name?.toLowerCase() === 'accountant';
    const isSuperAdmin = admin?.Role?.name?.toLowerCase() === 'super admin' || admin?.RoleId === 1;
    const isInventoryManager = admin?.Role?.name?.toLowerCase()?.includes('inventory');
    const isAffiliateManager = admin?.Role?.name?.toLowerCase()?.includes('affiliate');

    // ✅ match both /order and /order/:id as active
    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const navLinkStyle = (path) => ({
        bgcolor: 'transparent',
        borderLeft: isActive(path) ? '4px solid #5E7A60' : '4px solid transparent',
        pl: 2,
        borderRadius: isActive(path) ? '0 8px 8px 0' : '8px',
        mx: 1,
        width: 'calc(100% - 16px)',
        '&:hover': { bgcolor: isActive(path) ? 'rgba(94, 122, 96, 0.08)' : 'rgba(0, 0, 0, 0.04)' }
    });

    const navTextStyle = (path) => ({
        color: isActive(path) ? '#5E7A60' : '#5C5854',
        fontWeight: isActive(path) ? '600' : '400',
        '&:hover': { color: '#5E7A60' },
    });

    return (
        <Box>
            <AppBar
                open={open}
                position='relative'
                sx={{
                    marginTop: '57px',
                    bgcolor: 'white',
                    color: 'black',
                    boxShadow: 1
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="close drawer"
                        onClick={handleDrawerClose}
                        edge="start"
                        sx={{ mr: 2, ...(!open && { display: 'none' }) }}
                    >
                        {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{ mr: 2, ...(open && { display: 'none' }) }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div">
                        {isAccountant ? "Accountant Workspace" : isInventoryManager ? "Inventory Manager Workspace" : admin?.Role?.name?.toLowerCase()?.includes('affiliate') ? "Affiliate Manager Workspace" : "Super Admin Dashboard"}
                    </Typography>
                </Toolbar>
            </AppBar>

            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        },
                    },
                }}
                variant={isMobile ? "temporary" : "persistent"}
                anchor="left"
                open={open}
                onClose={handleDrawerClose}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
            >
                <Divider />
                {(isSuperAdmin || isAccountant || isInventoryManager || isAffiliateManager || (Array.isArray(permissions) && permissions.length > 0)) && (
                    <List sx={{ marginTop: '57px' }}>

                        {/* ── Dashboard ── */}
                        {(isSuperAdmin || permissions[0]?.view || isAccountant) && !isInventoryManager && (
                            <Link to={'/'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                <ListItemButton sx={navLinkStyle('/')}>
                                    <ListItemIcon>
                                        <DashboardIcon sx={{ color: isActive('/') ? '#5E7A60' : 'inherit' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={<Typography sx={navTextStyle('/')}>Dashboard</Typography>} />
                                </ListItemButton>
                            </Link>
                        )}

                        {/* ── Reports ── */}
                        {(isSuperAdmin || isAccountant) && (
                            <Link to={'/reports'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                <ListItemButton sx={navLinkStyle('/reports')}>
                                    <ListItemIcon>
                                        <AssessmentOutlinedIcon sx={{ color: isActive('/reports') ? '#5E7A60' : 'inherit' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={<Typography sx={navTextStyle('/reports')}>Reports & Analytics</Typography>} />
                                </ListItemButton>
                            </Link>
                        )}

                        {/* ── Accounts ── */}
                        {(isSuperAdmin || isAccountant || permissions[3]?.view) && !isInventoryManager && (
                            <>
                                <ListItemButton onClick={handleClickAccounts}>
                                    <ListItemIcon><AccountBalanceWalletIcon /></ListItemIcon>
                                    <ListItemText primary="Accounts" />
                                    {isAccounts ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={isAccounts} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        <Link to={'/withdrawal'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                            <ListItemButton sx={navLinkStyle('/withdrawal')}>
                                                <ListItemIcon />
                                                <ListItemText primary={<Typography sx={navTextStyle('/withdrawal')}>Withdrawals</Typography>} />
                                            </ListItemButton>
                                        </Link>
                                        {(isAccountant || isSuperAdmin) && (
                                            <Link to={'/expense'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/expense')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/expense')}>Company Expenses</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {isSuperAdmin && (
                                            <Link to={'/limit-requests'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/limit-requests')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/limit-requests')}>Limit Requests</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                    </List>
                                </Collapse>
                            </>
                        )}

                        {/* ── Content ── */}
                        {!isAccountant && (isSuperAdmin || permissions[5]?.view || permissions[6]?.view) && !isInventoryManager && (
                            <>
                                <ListItemButton onClick={handleClickContent}>
                                    <ListItemIcon><SourceIcon /></ListItemIcon>
                                    <ListItemText primary="Content" />
                                    {isContent ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={isContent} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {(isSuperAdmin || permissions[5]?.view) && (
                                            <Link to={'/faq'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/faq')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/faq')}>FAQ</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[6]?.view) && (
                                            <Link to={'/page'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/page')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/page')}>Page</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                    </List>
                                </Collapse>
                            </>
                        )}

                        {/* ── Masters ── */}
                        {!isAccountant && (isSuperAdmin || permissions[10]?.view || permissions[9]?.view || isInventoryManager) && (
                            <>
                                <ListItemButton onClick={handleClick}>
                                    <ListItemIcon><TableViewIcon /></ListItemIcon>
                                    <ListItemText primary="Masters" />
                                    {isMasters ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={isMasters} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {(isSuperAdmin || permissions[10]?.view) && !isInventoryManager && (
                                            <Link to={'/category'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/category')}>
                                                    <ListItemIcon>
                                                        <CategoryOutlinedIcon fontSize="small" sx={{ color: isActive('/category') ? '#5E7A60' : 'inherit' }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography sx={navTextStyle('/category')}>Category</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[9]?.view) && !isInventoryManager && (
                                            <Link to={'/product'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/product')}>
                                                    <ListItemIcon>
                                                        <Inventory2OutlinedIcon fontSize="small" sx={{ color: isActive('/product') ? '#5E7A60' : 'inherit' }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography sx={navTextStyle('/product')}>Product</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[9]?.view || isInventoryManager) && (
                                            <>
                                                <Link to={'/distributor'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                    <ListItemButton sx={navLinkStyle('/distributor')}>
                                                        <ListItemIcon>
                                                            <CallIcon fontSize="small" sx={{ color: isActive('/distributor') ? '#5E7A60' : 'inherit' }} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography sx={navTextStyle('/distributor')}>Distributors</Typography>} />
                                                    </ListItemButton>
                                                </Link>
                                                <Link to={'/inventory'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                    <ListItemButton sx={navLinkStyle('/inventory')}>
                                                        <ListItemIcon>
                                                            <Inventory2OutlinedIcon fontSize="small" sx={{ color: isActive('/inventory') ? '#5E7A60' : 'inherit' }} />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography sx={navTextStyle('/inventory')}>Inventory Management</Typography>} />
                                                    </ListItemButton>
                                                </Link>
                                            </>
                                        )}
                                        {(isSuperAdmin || permissions[9]?.view) && !isInventoryManager && (
                                            <Link to={'/reviews'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/reviews')}>
                                                    <ListItemIcon>
                                                        <RateReviewOutlinedIcon fontSize="small" sx={{ color: isActive('/reviews') ? '#5E7A60' : 'inherit' }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography sx={navTextStyle('/reviews')}>Product Reviews</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                    </List>
                                </Collapse>
                            </>
                        )}

                        {(isSuperAdmin || isAccountant || permissions[3]?.view) && !isInventoryManager && (
                            <Link to={'/order'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                <ListItemButton sx={navLinkStyle('/order')}>
                                    <ListItemIcon>
                                        <ShoppingBagOutlinedIcon sx={{ color: isActive('/order') ? '#5E7A60' : 'inherit' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={<Typography sx={navTextStyle('/order')}>Orders</Typography>} />
                                </ListItemButton>
                            </Link>
                        )}

                        {/* ── Users ── */}
                        {!isAccountant && (isSuperAdmin || permissions[2]?.view || permissions[3]?.view || permissions[4]?.view) && !isInventoryManager && (
                            <>
                                <ListItemButton onClick={handleClickUser}>
                                    <ListItemIcon><SourceIcon /></ListItemIcon>
                                    <ListItemText primary="Users" />
                                    {isUser ? <ExpandLess /> : <ExpandMore />}
                                </ListItemButton>
                                <Collapse in={isUser} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {(isSuperAdmin || permissions[4]?.view) && (
                                            <Link to={'/sub-admin'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/sub-admin')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/sub-admin')}>Sub Admin</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[3]?.view) && (
                                            <Link to={'/affiliate'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/affiliate')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/affiliate')}>Affiliates</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[3]?.view) && (
                                            <Link to={'/withdrawal'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/withdrawal')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/withdrawal')}>Withdrawals</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[3]?.view) && (
                                            <Link to={'/affiliate-rewards'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/affiliate-rewards')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/affiliate-rewards')}>Affiliate Rewards</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                        {(isSuperAdmin || permissions[2]?.view) && (
                                            <Link to={'/user'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                                <ListItemButton sx={navLinkStyle('/user')}>
                                                    <ListItemIcon />
                                                    <ListItemText primary={<Typography sx={navTextStyle('/user')}>Users</Typography>} />
                                                </ListItemButton>
                                            </Link>
                                        )}
                                    </List>
                                </Collapse>
                            </>
                        )}

                        {/* ── Contact Us ── */}
                        {!isAccountant && (isSuperAdmin || permissions[1]?.view) && !isInventoryManager && (
                            <Link to={'/contact-us'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                <ListItemButton sx={navLinkStyle('/contact-us')}>
                                    <ListItemIcon><CallIcon /></ListItemIcon>
                                    <ListItemText primary={<Typography sx={navTextStyle('/contact-us')}>Contact Us</Typography>} />
                                </ListItemButton>
                            </Link>
                        )}

                        {/* ── Setting ── */}
                        <ListItemButton onClick={handleClickSetting}>
                            <ListItemIcon><SettingsOutlinedIcon /></ListItemIcon>
                            <ListItemText primary="Setting" />
                            {isSetting ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={isSetting} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link to={'/profile-setting'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                    <ListItemButton sx={navLinkStyle('/profile-setting')}>
                                        <ListItemIcon />
                                        <ListItemText primary={<Typography sx={navTextStyle('/profile-setting')}>Profile</Typography>} />
                                    </ListItemButton>
                                </Link>
                                 {!isAccountant && (isSuperAdmin || permissions[7]?.view) && !isInventoryManager && (
                                    <Link to={'/role'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                        <ListItemButton sx={navLinkStyle('/role')}>
                                            <ListItemIcon />
                                            <ListItemText primary={<Typography sx={navTextStyle('/role')}>Roles</Typography>} />
                                        </ListItemButton>
                                    </Link>
                                )}
                                {isSuperAdmin && (
                                    <Link to={'/bank-setting'} style={{ textDecoration: 'none' }} onClick={handleLinkClick}>
                                        <ListItemButton sx={navLinkStyle('/bank-setting')}>
                                            <ListItemIcon />
                                            <ListItemText primary={<Typography sx={navTextStyle('/bank-setting')}>Bank Settings</Typography>} />
                                        </ListItemButton>
                                    </Link>
                                )}
                            </List>
                        </Collapse>

                    </List>
                )}
            </Drawer>
        </Box>
    );
}